/**
 * JavaScript 代码生成器
 * 生成使用 fetch API 的 JavaScript HTTP 请求代码
 */

import { BaseCodeGenerator } from './base.js';

export class JavaScriptGenerator extends BaseCodeGenerator {
  constructor() {
    super({
      language: 'javascript',
      library: 'fetch',
      indent: '  ', // JavaScript 标准缩进 2 个空格
      maxLineLength: 80
    });
  }

  /**
   * 生成完整的 JavaScript 代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 生成的 JavaScript 代码
   */
  generate(parsedCurl) {
    this.validateParsedCurl(parsedCurl);

    const requestCode = this.generateRequestCode(parsedCurl);
    const executionCode = this.generateExecutionCode(parsedCurl);

    const parts = [];

    // 添加请求代码
    parts.push(requestCode);

    // 添加执行代码
    if (executionCode) {
      parts.push('');
      parts.push(executionCode);
    }

    return parts.join('\n');
  }

  /**
   * 生成请求代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 请求代码
   */
  generateRequestCode(parsedCurl) {
    const lines = [];

    // 添加注释
    lines.push(this.generateComment(`发送 ${parsedCurl.method} 请求到 ${parsedCurl.url}`));

    // 构建 fetch 参数
    const fetchParams = this.buildFetchParams(parsedCurl);

    // 生成 fetch 调用
    let fetchCall = 'fetch(';
    fetchCall += `"${this.escapeString(parsedCurl.url)}"`;

    if (fetchParams.length > 0) {
      fetchCall += ', {\n';
      fetchCall += this.indentCode(fetchParams.join(',\n'), 1);
      fetchCall += '\n}';
    }

    fetchCall += ')';
    lines.push(fetchCall);

    return lines.join('\n');
  }

  /**
   * 生成执行代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 执行代码
   */
  generateExecutionCode(parsedCurl) {
    const lines = [];

    lines.push('.then(response => {');
    lines.push('  console.log(`Status: ${response.status} ${response.statusText}`);');
    lines.push('  ');
    lines.push('  return response.text(); // 或 response.json() 如果是 JSON');
    lines.push('})');
    lines.push('.then(data => {');
    lines.push('  console.log(data);');
    lines.push('})');
    lines.push('.catch(error => {');
    lines.push('  console.error(\'Error:\', error);');
    lines.push('});');

    return lines.join('\n');
  }

  /**
   * 构建 fetch 参数
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {Array} fetch 参数数组
   */
  buildFetchParams(parsedCurl) {
    const params = [];

    // HTTP 方法
    if (parsedCurl.method !== 'GET') {
      params.push(`method: "${parsedCurl.method}"`);
    }

    // 请求头
    if (Object.keys(parsedCurl.headers).length > 0) {
      params.push(this.generateHeadersParam(parsedCurl.headers));
    }

    // 请求体
    if (parsedCurl.data.type !== 'none') {
      params.push(this.generateBodyParam(parsedCurl.data));
    }

    // 其他选项
    const options = this.generateOptionsParams(parsedCurl);
    params.push(...options);

    return params;
  }

  /**
   * 生成请求头参数
   * @param {Object} headers - 请求头对象
   * @returns {string} 请求头参数
   */
  generateHeadersParam(headers) {
    const lines = ['headers: {'];

    Object.entries(headers).forEach(([key, value], index, array) => {
      const comma = index < array.length - 1 ? ',' : '';
      lines.push(`  "${this.escapeString(key)}": "${this.escapeString(value)}"${comma}`);
    });

    lines.push('}');

    return lines.join('\n');
  }

  /**
   * 生成请求体参数
   * @param {Object} data - 数据对象
   * @returns {string} 请求体参数
   */
  generateBodyParam(data) {
    switch (data.type) {
      case 'json':
        return this.generateJsonBodyParam(data.content);
      case 'form':
        return this.generateFormBodyParam(data.content);
      case 'raw':
        return this.generateRawBodyParam(data.content);
      case 'binary':
        return this.generateBinaryBodyParam(data.content);
      case 'urlencode':
        return this.generateUrlencodeBodyParam(data.content);
      default:
        return '';
    }
  }

  /**
   * 生成 JSON 请求体参数
   * @param {string} content - JSON 内容
   * @returns {string} JSON 请求体参数
   */
  generateJsonBodyParam(content) {
    return `body: JSON.stringify(${content})`;
  }

  /**
   * 生成表单请求体参数
   * @param {string} content - 表单内容
   * @returns {string} 表单请求体参数
   */
  generateFormBodyParam(content) {
    return `body: "${this.escapeString(content)}"`;
  }

  /**
   * 生成原始请求体参数
   * @param {string} content - 原始内容
   * @returns {string} 原始请求体参数
   */
  generateRawBodyParam(content) {
    return `body: "${this.escapeString(content)}"`;
  }

  /**
   * 生成二进制请求体参数
   * @param {string} content - 二进制内容
   * @returns {string} 二进制请求体参数
   */
  generateBinaryBodyParam(content) {
    return `body: ${content}`;
  }

  /**
   * 生成 URL 编码请求体参数
   * @param {string} content - URL 编码内容
   * @returns {string} URL 编码请求体参数
   */
  generateUrlencodeBodyParam(content) {
    return `body: new URLSearchParams("${this.escapeString(content)}")`;
  }

  /**
   * 生成选项参数
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {Array} 选项参数数组
   */
  generateOptionsParams(parsedCurl) {
    const params = [];

    // 认证
    if (parsedCurl.auth.type !== 'none') {
      // 在请求头中处理认证
    }

    // Cookie
    if (Object.keys(parsedCurl.cookies).length > 0) {
      // Cookie 通常在请求头中处理
    }

    // 重定向
    if (parsedCurl.followRedirects) {
      params.push('redirect: "follow"');
    } else {
      params.push('redirect: "manual"');
    }

    // 超时（fetch API 原生不支持，需要使用 AbortController）
    if (parsedCurl.connectTimeout > 0 || parsedCurl.maxTime > 0) {
      // 超时需要额外的处理逻辑
    }

    return params;
  }

  /**
   * 生成认证代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 认证代码
   */
  generateAuthCode(parsedCurl) {
    if (parsedCurl.auth.type === 'none') {
      return '';
    }

    const lines = [];

    switch (parsedCurl.auth.type) {
      case 'basic':
        lines.push(this.generateBasicAuthCode(parsedCurl.auth));
        break;
      case 'bearer':
        lines.push(this.generateBearerAuthCode(parsedCurl.auth));
        break;
      case 'digest':
        lines.push(this.generateDigestAuthCode(parsedCurl.auth));
        break;
    }

    return lines.join('\n');
  }

  /**
   * 生成基本认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 基本认证代码
   */
  generateBasicAuthCode(auth) {
    if (auth.username && auth.password !== undefined) {
      const credentials = btoa(`${auth.username}:${auth.password || ''}`);
      return `headers["Authorization"] = "Basic ${credentials}";`;
    }
    return '';
  }

  /**
   * 生成 Bearer 认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} Bearer 认证代码
   */
  generateBearerAuthCode(auth) {
    return `headers["Authorization"] = "Bearer ${this.escapeString(auth.token)}";`;
  }

  /**
   * 生成摘要认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 摘要认证代码
   */
  generateDigestAuthCode(auth) {
    // JavaScript 的 fetch API 不原生支持摘要认证
    // 需要使用第三方库或手动实现
    return `// 摘要认证需要额外的实现或第三方库`;
  }

  /**
   * 生成 Cookie 代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} Cookie 代码
   */
  generateCookiesCode(parsedCurl) {
    if (Object.keys(parsedCurl.cookies).length === 0) {
      return '';
    }

    const cookieString = Object.entries(parsedCurl.cookies)
      .map(([key, value]) => `${this.escapeString(key)}=${this.escapeString(value)}`)
      .join('; ');

    return `headers["Cookie"] = "${cookieString}";`;
  }

  /**
   * 生成超时处理代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 超时处理代码
   */
  generateTimeoutCode(parsedCurl) {
    const timeout = parsedCurl.connectTimeout || parsedCurl.maxTime;
    if (timeout <= 0) {
      return '';
    }

    const lines = [];
    lines.push('// 设置超时');
    lines.push(`const controller = new AbortController();`);
    lines.push(`const timeoutId = setTimeout(() => controller.abort(), ${timeout * 1000});`);
    lines.push('');
    lines.push('fetch(url, {');
    lines.push('  signal: controller.signal,');
    lines.push('  // ... 其他选项');
    lines.push('})');
    lines.push('.then(response => {');
    lines.push('  clearTimeout(timeoutId);');
    lines.push('  // 处理响应');
    lines.push('})');
    lines.push('.catch(error => {');
    lines.push('  if (error.name === \'AbortError\') {');
    lines.push('    console.error(\'请求超时\');');
    lines.push('  } else {');
    lines.push('    console.error(\'请求失败:\', error);');
    lines.push('  }');
    lines.push('});');

    return lines.join('\n');
  }

  /**
   * 生成代理代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 代理代码
   */
  generateProxyCode(parsedCurl) {
    if (!parsedCurl.proxy) {
      return '';
    }

    // JavaScript 的 fetch API 不支持代理
    // 需要使用服务端代理或第三方库
    return `// 代理功能需要服务端支持或第三方库\n// 代理: ${parsedCurl.proxy}`;
  }

  /**
   * 生成 SSL 验证代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} SSL 验证代码
   */
  generateSslCode(parsedCurl) {
    if (!parsedCurl.insecure) {
      return '';
    }

    // JavaScript 的 fetch API 在 HTTPS 下总是验证证书
    // 要跳过 SSL 验证需要使用 Node.js 的 https 模块
    return `// 跳过 SSL 验证在浏览器中不可用\n// 需要在 Node.js 环境中使用 https 模块`;
  }

  /**
   * 生成完整的请求代码（包含所有功能）
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 完整请求代码
   */
  generateFullRequestCode(parsedCurl) {
    const lines = [];

    // 变量声明
    lines.push('// 请求配置');
    lines.push(`const url = "${this.escapeString(parsedCurl.url)}";`);
    lines.push('const headers = {};');

    // 设置请求头
    if (Object.keys(parsedCurl.headers).length > 0) {
      lines.push('');
      Object.entries(parsedCurl.headers).forEach(([key, value]) => {
        lines.push(`headers["${this.escapeString(key)}"] = "${this.escapeString(value)}";`);
      });
    }

    // 设置认证
    const authCode = this.generateAuthCode(parsedCurl);
    if (authCode) {
      lines.push('');
      lines.push(authCode);
    }

    // 设置 Cookie
    const cookieCode = this.generateCookiesCode(parsedCurl);
    if (cookieCode) {
      lines.push('');
      lines.push(cookieCode);
    }

    // 构建请求选项
    lines.push('');
    lines.push('// 构建请求选项');
    lines.push('const options = {');

    if (parsedCurl.method !== 'GET') {
      lines.push(`  method: "${parsedCurl.method}",`);
    }

    lines.push('  headers,');

    // 请求体
    if (parsedCurl.data.type !== 'none') {
      lines.push(`  ${this.generateBodyParam(parsedCurl.data)},`);
    }

    // 重定向
    if (parsedCurl.followRedirects) {
      lines.push('  redirect: "follow",');
    }

    // 移除最后一个逗号
    const lastLine = lines[lines.length - 1];
    if (lastLine.endsWith(',')) {
      lines[lines.length - 1] = lastLine.slice(0, -1);
    }

    lines.push('};');

    // 生成 fetch 调用
    lines.push('');
    lines.push('// 发送请求');
    lines.push('fetch(url, options)');
    lines.push('  .then(response => {');
    lines.push('    if (!response.ok) {');
    lines.push('      throw new Error(`HTTP error! status: ${response.status}`);');
    lines.push('    }');
    lines.push('    return response.text(); // 或 response.json()');
    lines.push('  })');
    lines.push('  .then(data => {');
    lines.push('    console.log("Response:", data);');
    lines.push('  })');
    lines.push('  .catch(error => {');
    lines.push('    console.error("Error:", error);');
    lines.push('  });');

    return lines.join('\n');
  }

  /**
   * 生成注释
   * @param {string} text - 注释文本
   * @returns {string} 注释代码
   */
  generateComment(text) {
    return `// ${text}`;
  }

  /**
   * 转义字符串（JavaScript 风格）
   * @param {string} str - 要转义的字符串
   * @returns {string} 转义后的字符串
   */
  escapeString(str) {
    return str
      .replace(/\\/g, '\\\\')  // 反斜杠
      .replace(/"/g, '\\"')    // 双引号
      .replace(/'/g, "\\'")    // 单引号
      .replace(/\n/g, '\\n')   // 换行符
      .replace(/\r/g, '\\r')   // 回车符
      .replace(/\t/g, '\\t')   // 制表符
      .replace(/\b/g, '\\b')   // 退格符
      .replace(/\f/g, '\\f');  // 换页符
  }
}